/**
 * Pure, framework-free redaction of secrets that appear in URLs.
 *
 * PostHog attaches `$current_url` and `$pathname` to every event it sends,
 * read straight from `window.location`. Three of our routes carry a bearer
 * secret in the PATH rather than a header:
 *
 *   /upload/:token             - unauthenticated phone upload
 *   /worksheet-upload/:token   - unauthenticated worksheet upload
 *   /auth/reset-password/:token - password reset
 *
 * Left alone, a student opening a password-reset link would ship a live reset
 * token to a third-party analytics vendor, where it would sit in an event log
 * that many people can read. These are credentials, not identifiers.
 *
 * Kept side-effect free and vendor-agnostic so it can be unit tested with
 * `node --test`; posthog.js wires it into the SDK's sanitize hook. Same split
 * as errorFilters.js / sentry.js.
 */

/** Path prefixes whose NEXT segment is a secret, not an identifier. */
const SECRET_PATH_PREFIXES = [
  '/upload/',
  '/worksheet-upload/',
  '/auth/reset-password/',
];

/** Query parameters that carry secrets regardless of which route they're on. */
const SECRET_QUERY_PARAMS = [
  'token',
  'code',
  'access',
  'refresh',
  'signup_token',
];

const REDACTED = '[redacted]';

/**
 * Replace the secret segment of a path with a placeholder, leaving the route
 * shape intact so it still groups usefully in analytics.
 *
 * `/upload/abc123` becomes `/upload/[redacted]`.
 */
export function redactPath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return pathname;

  for (const prefix of SECRET_PATH_PREFIXES) {
    if (!pathname.startsWith(prefix)) continue;
    const rest = pathname.slice(prefix.length);
    if (!rest) return pathname;
    // Keep anything after the secret segment (nested routes), drop the secret.
    const slash = rest.indexOf('/');
    return slash === -1
      ? `${prefix}${REDACTED}`
      : `${prefix}${REDACTED}${rest.slice(slash)}`;
  }

  return pathname;
}

/**
 * Redact both the path and any sensitive query parameters of a full URL.
 * Returns the input unchanged if it cannot be parsed - never throws, because
 * this runs inside the analytics hot path.
 */
export function redactUrl(url) {
  if (typeof url !== 'string' || !url) return url;

  try {
    const parsed = new URL(url);
    parsed.pathname = redactPath(parsed.pathname);
    for (const param of SECRET_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, REDACTED);
      }
    }
    return parsed.toString();
  } catch (_) {
    // Relative or malformed - fall back to path-only redaction so a parse
    // failure degrades to "less redaction", never to "throws in production".
    return redactPath(url);
  }
}

/** Event properties whose values are URLs PostHog populates for us. */
const URL_PROPERTIES = [
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
];

const PATH_PROPERTIES = ['$pathname', '$initial_pathname'];

/**
 * Person-property bags that ride INSIDE an event's properties.
 *
 * The initial-URL properties are written here rather than at the top level, so
 * a flat pass over `properties` never sees them. That matters most for exactly
 * the worst case: a student whose FIRST visit is a password-reset link gets
 * that URL recorded as `$initial_current_url` on their person record, where it
 * persists long after the event itself has aged out.
 */
const NESTED_PROPERTY_BAGS = ['$set', '$set_once'];

/** Redact the URL and path properties of one flat bag, in place. */
function redactBag(bag) {
  for (const key of URL_PROPERTIES) {
    if (typeof bag[key] === 'string') {
      bag[key] = redactUrl(bag[key]);
    }
  }
  for (const key of PATH_PROPERTIES) {
    if (typeof bag[key] === 'string') {
      bag[key] = redactPath(bag[key]);
    }
  }
}

/**
 * Redact every URL-bearing property of an event's property bag.
 *
 * Runs on every outbound event, so it must be cheap and must never throw - a
 * redaction bug that took down event capture would be worse than the leak it
 * prevents.
 */
export function sanitiseProperties(properties) {
  if (!properties || typeof properties !== 'object') return properties;

  try {
    redactBag(properties);
    for (const key of NESTED_PROPERTY_BAGS) {
      const nested = properties[key];
      if (nested && typeof nested === 'object') {
        redactBag(nested);
      }
    }
  } catch (_) {
    /* never let redaction break capture */
  }

  return properties;
}

/**
 * The hook handed to the PostHog SDK's `before_send`.
 *
 * Kept here rather than in posthog.js so the whole redaction decision stays in
 * one pure, vendor-free module that `node --test` can exercise; posthog.js
 * only wires it up.
 *
 * `before_send` replaces the deprecated `sanitize_properties`, and the
 * contract is different in two ways that both bite silently:
 *
 *   - It receives the whole capture result and must RETURN it. Returning the
 *     property bag instead - the shape the old hook returned - makes the SDK
 *     treat every event as malformed.
 *   - Returning null DROPS the event. That is a feature for sampling and a
 *     footgun here: redaction must never delete data, so this always returns
 *     the event, even when handed something it does not recognise.
 */
export function sanitiseEvent(event) {
  if (!event || typeof event !== 'object') return event;

  sanitiseProperties(event.properties);
  return event;
}
